import { useFindableSimulationData } from '../../hooks/useFindableSimulationData';
import { useGlobalization } from '../../contexts';
import * as Utils from './utils/SimulationDataFinderUtils';
import * as DataFinder from './DataFinder';
import * as Dialog from './Dialog';
import * as React from 'react';
import {
	generateSimulationObjects,
	parseSimulationObjects,
} from './utils/SimulationDataFinderUtils';
import { useSelectInfoWaterProDbid } from '../../selectors';
import { keyforSimulation, STATUS_ENUM, type SharedStoreState } from '../../types';
import { useSelector } from 'react-redux';

/**
 * A simulation data object that represents a single, complete selection from the DataFinder component.
 * This is used to interact with the saas_edge_analytics API, so underscores are used for compatibility.
 *
 * Compare with `Selection` in `DataFinder.tsx`.
 */
export type SimulationDataObject = {
	database_id: string;
	analysis_group_id: string;
	result_id: string;
	table_id: string;
	object_id: string;
	attribute: string;
};

/**
 * The props for the `SimulationDataFinderDialog` component.
 *
 * @param dbid the database id to use for the simulation data.  If not provided, the component will attempt to fetch it from the redux store.
 * @param onSubmit a callback function that is called when the user submits their selection.  The selected simulation data objects are passed as an argument.
 * @param initialSelections an array of `SimulationDataObject` entities that should be pre-selected when the dialog is opened.
 */
export type SimulationDataFinderDialogProps = {
	dbid?: string;
	onSubmit?: (simulationDataObjects: SimulationDataObject[]) => void;
	initialSelections?: Partial<SimulationDataObject>[];
};

/**
 * A dialog preset that renders an `DataFinder` component inside a modal that
 * enables users to select one or more `SimulationDataObject` entities.
 * see https://stylovyze.info360-dev.com/?path=/story/data-finder-simulation-data for usage details
 */
export const SimulationDataFinderDialog = React.forwardRef<
	HTMLInputElement,
	SimulationDataFinderDialogProps
>((props, ref) => {
	const { dbid, onSubmit, initialSelections } = props;
	const { t } = useGlobalization();
	const [isModalOpen, setIsModalOpen] = React.useState(false);
	const [_selections, setSelections] = React.useState<DataFinder.Selection[]>(
		initialSelections ? parseSimulationObjects(initialSelections) : [],
	);

	const _dbid = useSelectInfoWaterProDbid();
	const simulations = useSelector((state: SharedStoreState) => state.infoWaterPro.simulations);

	const selections: DataFinder.Selection[] = React.useMemo(() => {
		const databaseId = dbid || _dbid.data || '';
		const analysisGroupId = _selections.find(s => s.categoryId === 'analysis-group');
		if (!analysisGroupId) return _selections;
		const simsKey = keyforSimulation(databaseId, analysisGroupId.optionId);

		const userSelections = _selections.map(s => {
			if (s.categoryId !== 'simulation') return s;
			const sims = simulations[simsKey];
			if (!sims) return s;

			const simulationId = Number(s.optionId);
			const simulation = sims.data?.find(s => s.oidhistory?.includes(simulationId)); // prettier-ignore
			if (!simulation) return s;

			return {
				...s,
				optionId: simulation.oid.toString(),
			};
		});

		const simulationIds = simulations[simsKey]?.data?.map(({oid}) => +oid);
		const selectedSimulationId = userSelections.find(({categoryId}) => categoryId === 'simulation')?.optionId;
		const anaylisisGroupSelection = userSelections.length ? [{...userSelections[0]}] : [];
		if(!selectedSimulationId) {
			return anaylisisGroupSelection; 
		}
		const isSimulationDeleted = !simulationIds?.includes(+selectedSimulationId);
		if (isSimulationDeleted) {
			return anaylisisGroupSelection;
		}

		return userSelections;
	}, [_selections, simulations, _dbid.data, dbid]);

	const handleSelection = (selection: DataFinder.Selection) => {
		const action = Utils.getSelectionAction(selections, selection);
		setSelections(Utils.getNextSelections(action, selections, selection));
	};

	const [_selectedSimulationData, setSelectedSimulationData] = React.useState<
		SimulationDataObject[]
	>(() => initialSelections as SimulationDataObject[] || []);

	const selectedSimulationData: SimulationDataObject[] = React.useMemo(() => {
		const databaseId = dbid || _dbid.data || '';

		return _selectedSimulationData.map(s => {
			const analysisGroupId = s.analysis_group_id;

			const key = keyforSimulation(databaseId, analysisGroupId);
			const sims = simulations[key];
			if (!sims) return s;

			const simulationId = Number(s.result_id);
			const simulation = sims.data?.find(s => s.oidhistory?.includes(simulationId)); // prettier-ignore
			if (!simulation) return s;

			return {
				...s,
				result_id: simulation.oid.toString(),
			};
		});
	}, [_selectedSimulationData, simulations, _dbid.data, dbid]);

	const [customerDbid, setCustomerDbid] =
		React.useState<string | undefined>(dbid);

	const dbidFromRedux = useSelectInfoWaterProDbid();

	React.useEffect(() => {
		if (!dbid && dbidFromRedux.status === STATUS_ENUM.SUCCESS) {
			setCustomerDbid(dbidFromRedux.data);
		}
	}, [dbidFromRedux]);

	const data = useFindableSimulationData(customerDbid ?? '', selections);

	function deleteSimulation(
		index: number,
		onSubmit:
			| ((simulationDataObjects: SimulationDataObject[]) => void)
			| undefined,
	): void {
		const newSelectedSimulationData = selectedSimulationData.filter(
			(_, i) => i !== index,
		);
		setSelectedSimulationData(newSelectedSimulationData);
		const newSelections = parseSimulationObjects(newSelectedSimulationData);
		setSelections(newSelections);
		onSubmit && customerDbid && onSubmit(newSelectedSimulationData);
	}

	return (
		<Dialog.Root>
			<Dialog.Trigger
				InputProps={Utils.INPUT_PROPS}
				InputLabelProps={Utils.INPUT_LABEL_PROPS}
				label={t('Simulation Data')}
				onClick={() => setIsModalOpen(true)}
				fullWidth={true}
				selectedSimulationData={selectedSimulationData}
				onDelete={index => deleteSimulation(index, onSubmit)}
			/>
			<Dialog.Modal
				open={isModalOpen}
				fullWidth={true}
				maxWidth={false}
				onClose={() => setIsModalOpen(false)}>
				<Dialog.Header>
					<Dialog.Title>{t('Select simulation data')}</Dialog.Title>
					<Dialog.ButtonArea>
						<Dialog.CancelButton
							onClick={() => {
								setIsModalOpen(false);
								setSelections([]);
							}}>
							{t('Cancel')}
						</Dialog.CancelButton>
						<Dialog.SubmitButton
							onClick={() => {
								const labelData = generateSimulationObjects(
									customerDbid || '',
									selections,
								);
								onSubmit && customerDbid && onSubmit(labelData);
								setSelectedSimulationData(labelData);
								setIsModalOpen(false);
							}}
							disabled={!Utils.validSelection(selections)}>
							{t('Confirm Selection')}
						</Dialog.SubmitButton>
					</Dialog.ButtonArea>
				</Dialog.Header>
				<Dialog.Main>
					<DataFinder.Root
						data={data}
						selections={selections}
						onSelection={handleSelection}
					/>
				</Dialog.Main>
			</Dialog.Modal>
		</Dialog.Root>
	);
});

SimulationDataFinderDialog.displayName = 'SimulationDataFinderDialog';
